import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { PrivateLayout, LoadingScreen } from "./layouts";
import { OfertaProvider } from "./features/oferta-personalizada/context/OfertaContext";

// Lazy loaded pages
const LoginPage = lazy(() =>
  import("./features/login").then((module) => ({ default: module.LoginPage })),
);
const LogoutPage = lazy(() =>
  import("./features/logout").then((module) => ({
    default: module.LogoutPage,
  })),
);
const CrearOfertaPage = lazy(() =>
  import("./features/crear-oferta").then((module) => ({
    default: module.CrearOfertaPage,
  })),
);
const OfertaPersonalizadaPage = lazy(() =>
  import("./features/oferta-personalizada").then((module) => ({
    default: module.OfertaPersonalizadaPage,
  })),
);
const MisOfertasPage = lazy(() =>
  import("./features/mis-ofertas").then((module) => ({
    default: module.MisOfertasPage,
  })),
);
const OfertaDetallePage = lazy(() =>
  import("./features/oferta-detalle").then((module) => ({
    default: module.OfertaDetallePage,
  })),
);
const GuardarOfertaPage = lazy(() =>
  import("./features/guardar-oferta").then((module) => ({
    default: module.GuardarOfertaPage,
  })),
);
const AgendarVisitaPage = lazy(() =>
  import("./features/agendar-visita").then((module) => ({
    default: module.AgendarVisitaPage,
  })),
);
const ErrorPage = lazy(() =>
  import("./features/error").then((module) => ({
    default: module.ErrorPage,
  })),
);
const HomePage = lazy(() =>
  import("./features/home").then((module) => ({
    default: module.HomePage,
  })),
);
const ProfilePage = lazy(() =>
  import("./features/profile").then((module) => ({
    default: module.ProfilePage,
  })),
);

export const router = createBrowserRouter([
  // ===================================
  // LOGIN - Sin wrapper (maneja OAuth internamente)
  // ===================================
  {
    path: "/",
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <LoginPage />
      </Suspense>
    ),
  },

  // ===================================
  // LOGOUT - Accesible siempre
  // ===================================
  {
    path: "/logout",
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <LogoutPage />
      </Suspense>
    ),
  },

  // ===================================
  // RUTAS PRIVADAS CON OFERTA PROVIDER
  // ===================================
  {
    element: <PrivateLayout ContextProvider={OfertaProvider} />,
    children: [
      {
        path: "/inicio",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: "/perfil",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ProfilePage />
          </Suspense>
        ),
      },
      {
        path: "ofertas/mis-ofertas",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <MisOfertasPage />
          </Suspense>
        ),
      },
      {
        path: "ofertas/crear-oferta",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <CrearOfertaPage />
          </Suspense>
        ),
      },
      {
        path: "ofertas/oferta-personalizada",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <OfertaPersonalizadaPage />
          </Suspense>
        ),
      },
      {
        path: "ofertas/guardar-oferta",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <GuardarOfertaPage />
          </Suspense>
        ),
      },
      {
        path: "ofertas/oferta-detalle/:pk",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <OfertaDetallePage />
          </Suspense>
        ),
      },
      {
        path: "ofertas/agendar-visita/:pk",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <AgendarVisitaPage />
          </Suspense>
        ),
      },
    ],
  },

  // ===================================
  // ERROR PAGE - Ruta pública para errores
  // ===================================
  {
    path: "/error",
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <ErrorPage />
      </Suspense>
    ),
  },

  // ===================================
  // 404 - Ruta catch-all
  // ===================================
  {
    path: "*",
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <ErrorPage
          code="404"
          title="Página no encontrada"
          message="La página que buscas no existe o ha sido movida."
          buttonText="Volver al inicio"
        />
      </Suspense>
    ),
  },
]);
